import React, { useState } from 'react';
import { Globe, ExternalLink, Rocket, Smartphone, MapPin, GraduationCap, Map, Radar, Tent } from 'lucide-react';

// ── BCH Ecosystem hub ───────────────────────────────────────────────
//
// A single grid page linking out to the family of Bitcoin Cash sites in the
// BCHNostr ecosystem. External links only — each opens in a new tab.

interface EcoSite {
  name: string;
  url: string;
  tagline: string;
  icon: React.ReactNode;
  /**
   * Explicit favicon URL(s) for sites whose icon lives at a non-standard path
   * (e.g. /logo.png or /icon-192.png). Tried before the generic guesses.
   */
  favicon?: string | string[];
}

const SITES: EcoSite[] = [
  {
    name: 'BCH Launch',
    url: 'https://bchlaunch.com',
    tagline: 'Spin up your own Bitcoin Cash site or project.',
    icon: <Rocket className="w-5 h-5" />,
  },
  {
    name: 'BCH School',
    url: 'https://bchschool.com',
    tagline: 'Learn Bitcoin Cash — guides and lessons for newcomers.',
    icon: <GraduationCap className="w-5 h-5" />,
  },
  {
    name: 'BCH Trail',
    url: 'https://bchtrail.com',
    tagline: 'Explore places and merchants along the BCH trail.',
    icon: <Map className="w-5 h-5" />,
    favicon: 'https://bchtrail.com/favicon.png',
  },
  {
    name: 'BCH Radar',
    url: 'https://bchradar.com',
    tagline: 'Track what’s happening across the Bitcoin Cash world.',
    icon: <Radar className="w-5 h-5" />,
  },
  {
    name: 'Survive on BCH',
    url: 'https://surviveonbch.com',
    tagline: 'Live on peer-to-peer digital cash. Two ways to play — spend your way through the week, or run the shop and choose how to get paid.',
    icon: <Tent className="w-5 h-5" />,
    favicon: 'https://surviveonbch.com/logo.png',
  },
  {
    name: 'BCH Pay',
    url: 'https://bchpay.cash',
    tagline: 'Free point-of-sale terminal — accept BCH anywhere.',
    icon: <Smartphone className="w-5 h-5" />,
    favicon: 'https://bchpay.cash/icon-192.png',
  },
  {
    name: 'BCH Local',
    url: 'https://bchlocal.com',
    tagline: 'Find local businesses that accept Bitcoin Cash.',
    icon: <MapPin className="w-5 h-5" />,
    favicon: 'https://bchlocal.com/favicon.svg',
  },
];

/**
 * Ordered list of favicon URLs to try for a site. Google's favicon service is
 * unreliable for sites it hasn't crawled/cached yet (blank result), so we try
 * the site's OWN favicons first, then fall back to Google, then DuckDuckGo.
 * The card cascades through these on each image load error, and finally falls
 * back to a lucide icon. All https, which the app CSP permits under img-src.
 */
function faviconCandidates(url: string, explicit?: string | string[]): string[] {
  // Any explicitly-declared favicon URLs take priority — some sites keep their
  // icon at a non-standard path (/logo.png, /icon-192.png) that guessing misses.
  const forced = explicit ? (Array.isArray(explicit) ? explicit : [explicit]) : [];
  try {
    const u = new URL(url);
    const origin = u.origin;
    const host = u.hostname;
    return [
      ...forced,
      // Site's own icons (most authoritative — works for brand-new domains).
      `${origin}/favicon.ico`,
      `${origin}/favicon.svg`,
      `${origin}/favicon.png`,
      `${origin}/apple-touch-icon.png`,
      // Third-party resolvers as fallbacks.
      `https://icons.duckduckgo.com/ip3/${host}.ico`,
      `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`,
    ];
  } catch {
    return forced;
  }
}

function SiteCard({ site }: { site: EcoSite }) {
  const candidates = faviconCandidates(site.url, site.favicon);
  // Index into `candidates`; when it runs past the end we render the icon.
  const [srcIdx, setSrcIdx] = useState(0);
  const favicon = candidates[srcIdx];
  const host = (() => { try { return new URL(site.url).hostname.replace(/^www\./, ''); } catch { return site.url; } })();

  return (
    <a
      href={site.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group glass-card !p-4 flex flex-col gap-3 hover:border-primary/40 hover:shadow-md transition-all"
    >
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary overflow-hidden">
          {favicon ? (
            <img
              src={favicon}
              alt=""
              width={28}
              height={28}
              loading="lazy"
              className="w-7 h-7 rounded"
              // Cascade to the next candidate URL; when exhausted, srcIdx goes
              // out of range and we fall back to the lucide icon.
              onError={() => setSrcIdx(i => i + 1)}
            />
          ) : (
            site.icon
          )}
        </div>
        <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
      <div>
        <p className="font-bold text-sm group-hover:text-primary transition-colors">{site.name}</p>
        <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{host}</p>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{site.tagline}</p>
    </a>
  );
}

export default function EcosystemPage() {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <Globe className="w-5 h-5 text-primary" /> BCH Ecosystem
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          The family of Bitcoin Cash sites — all in one place.
        </p>
      </div>

      <div className="p-4 max-w-3xl mx-auto">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {SITES.map(site => (
            <SiteCard key={site.url} site={site} />
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground text-center mt-6">
          Each site opens in a new tab. Building something for the BCH ecosystem?{' '}
          <a href="https://bchlaunch.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
            Launch it on BCH Launch
          </a>
          .
        </p>
      </div>
    </div>
  );
}
